import React, { useEffect, useState } from 'react';

import { DOCUMENT_TYPES } from '~constants/common';

import { useSearch } from '~libs/searchParams';
import { useToast } from '~libs/toast';

import { BulkActions, Button, ClickableIcon, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view-v3';
import { useDocumentBatchGeneration } from '~main/components/document-batch-jobs/useDocumentBatchGeneration';

import { columnsFactory } from '~support-at-home/forms/co-contributions/list';
import { searchFields } from '~support-at-home/forms/co-contributions/search';

import { useGetClientInvoiceStatusesQuery } from '~lookups/actions/clientInvoiceStatuses';
import { setTitle } from '~main/actions/login';
import { useGetClientInvoicesQuery } from '~support-at-home/actions/clientInvoices';

import CoContributionsWizard from './co-contributions-wizard';
import CoContributionsModal from './CoContributionsModal';

const CoContributions: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setParams] = useSearch('co-contributions-search', {});
  const search = (values: Record<string, any>) => setParams(values);

  const [wizardOpen, setWizardOpen] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [invoiceModalId, setInvoiceModalId] = useState<string | null>(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);

  const { generateSeparate, generateCombined, emailSeparate } = useDocumentBatchGeneration();
  const { showToast } = useToast();

  const openContribModal = (id: string) => {
    setInvoiceModalId(id);
    setModalOpen(true);
  };

  useEffect(() => {
    if (isActive) {
      setTitle('Client Co-Contributions');
    }
  }, [isActive]);

  const { data: statuses = [] } = useGetClientInvoiceStatusesQuery();
  const { data: invoices = [], isFetching } = useGetClientInvoicesQuery(searchParams);

  const bulkActionOptions = [
    { value: 'generate-separate', label: 'Generate PDFs' },
    { value: 'generate-combined', label: 'Generate Combined PDF', minimum: 2 },
    { value: 'email-separate', label: 'Email Client Invoices' },
  ];

  const handleBulkAction = async (action: string) => {
    if (selectedInvoiceIds.length === 0) return;

    try {
      switch (action) {
        case 'generate-separate':
          await generateSeparate(DOCUMENT_TYPES.CO_CONTRIBUTION.SINGLE, selectedInvoiceIds).unwrap();
          showToast('success', `Started generating ${selectedInvoiceIds.length} invoice PDF(s)`);
          break;
        case 'generate-combined':
          await generateCombined(DOCUMENT_TYPES.CO_CONTRIBUTION.COMBINED, selectedInvoiceIds).unwrap();
          showToast('success', 'Started generating combined invoice PDF');
          break;
        case 'email-separate':
          await emailSeparate(DOCUMENT_TYPES.CO_CONTRIBUTION.SINGLE, selectedInvoiceIds).unwrap();
          showToast('success', `Scheduled ${selectedInvoiceIds.length} invoice email(s)`);
          break;
        default:
          console.warn('Unknown bulk action:', action);
      }
      setSelectedInvoiceIds([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to process bulk action';
      showToast('danger', message);
      console.error('Bulk action failed:', error);
    }
  };

  return (
    <div className="p-2 co-contributions">
      <Toolbar
        id="co-contributions"
        rightCluster={
          <>
            <BulkActions
              id="co-contributions-bulk-actions"
              selectedIds={selectedInvoiceIds}
              onAction={handleBulkAction}
              actionOptions={bulkActionOptions}
            />
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="co-contributions-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <Button dataCy="co-contributions-new-button" size="sm" color="success" onClick={() => setWizardOpen(true)}>
              <Icon className="me-2" icon={faPlus} />
              New Invoice Batch
            </Button>
          </>
        }
      />
      <SearchBar
        isOpen={searchBarOpen}
        fields={searchFields(statuses)}
        initialValues={searchParams}
        onSubmit={search}
      />
      <TableView
        id="co-contributions-table"
        columns={columnsFactory(openContribModal)}
        data={invoices}
        isLoading={isFetching}
        emptyMessage="No co-contributions found"
        readonly
        striped
        canSelect
        selectedIds={selectedInvoiceIds}
        onRowSelectionChange={(invoiceIds) => setSelectedInvoiceIds((invoiceIds as (string | number)[]).map(String))}
      />
      <CoContributionsWizard isOpen={wizardOpen} setIsOpen={setWizardOpen} />
      <CoContributionsModal isOpen={modalOpen} setIsOpen={setModalOpen} clientId={invoiceModalId} />
    </div>
  );
};

export default CoContributions;
